import type { Exercise } from '../data/exercise-types';
import type { LoggedExercise, SetLog, WorkoutSession } from '../store/types';

export interface LoadSuggestion {
  weightKg?: number;
  reps?: number;
  reason: string;
  /** Drives the colour of the hint on the logging screen. */
  kind: 'increase' | 'hold' | 'deload' | 'first_time';
}

/** Epley estimate. Only meaningful up to about 12 reps. */
export function estimate1rm(weightKg: number, reps: number): number {
  if (reps <= 0) return 0;
  if (reps === 1) return weightKg;
  return Math.round(weightKg * (1 + reps / 30) * 10) / 10;
}

/** Round to something you can actually load on the bar or pick off the rack. */
function roundToIncrement(weight: number, increment: number): number {
  if (increment <= 0) return Math.round(weight * 2) / 2;
  return Math.round(weight / increment) * increment;
}

export function findLastPerformance(
  workouts: WorkoutSession[],
  exerciseId: string,
): { session: WorkoutSession; logged: LoggedExercise } | undefined {
  const sorted = [...workouts]
    .filter((w) => w.finishedAt)
    .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
  for (const session of sorted) {
    const logged = session.exercises.find(
      (e) => e.exerciseId === exerciseId && e.sets.some((s) => s.done),
    );
    if (logged) return { session, logged };
  }
  return undefined;
}

function workingSets(sets: SetLog[]): SetLog[] {
  return sets.filter((s) => s.done && s.reps > 0);
}

/**
 * Double progression: stay at a weight until every working set hits the top of
 * the rep range, then add the smallest useful jump and work back up. It is
 * boring and it is the reason people who use it keep getting stronger.
 */
export function suggestLoad(
  exercise: Exercise,
  workouts: WorkoutSession[],
  loadMultiplier = 1,
): LoadSuggestion {
  const last = findLastPerformance(workouts, exercise.id);
  const [lowReps, highReps] = exercise.repRange;

  if (!last) {
    return {
      reason:
        'First time logging this. Pick a weight you could do about two reps past the top of the range, and note what you used.',
      kind: 'first_time',
    };
  }

  const sets = workingSets(last.logged.sets);
  if (sets.length === 0) {
    return { reason: 'No completed sets logged last time.', kind: 'first_time' };
  }

  const topWeight = Math.max(...sets.map((s) => s.weightKg));
  const atTopWeight = sets.filter((s) => s.weightKg >= topWeight);
  const minReps = Math.min(...atTopWeight.map((s) => s.reps));
  const rpes = atTopWeight.map((s) => s.rpe).filter((r): r is number => typeof r === 'number');
  const avgRpe = rpes.length ? rpes.reduce((a, b) => a + b, 0) / rpes.length : undefined;

  const apply = (w: number) => roundToIncrement(w * loadMultiplier, exercise.incrementKg);

  // Bodyweight and timed work has no load to add — progress the reps instead.
  if (exercise.loadType === 'bodyweight' || exercise.loadType === 'timed') {
    const unit = exercise.loadType === 'timed' ? 'seconds' : 'reps';
    if (minReps >= highReps) {
      return {
        reps: highReps,
        reason: `You hit ${minReps} ${unit} on every set. Add load with a belt or move to a harder variation.`,
        kind: 'increase',
      };
    }
    return {
      reps: Math.min(highReps, minReps + 1),
      reason: `Last time: ${minReps} ${unit}. Beat it by one.`,
      kind: 'hold',
    };
  }

  if (minReps >= highReps && (avgRpe === undefined || avgRpe <= 8.5)) {
    const next = apply(topWeight + exercise.incrementKg);
    return {
      weightKg: next,
      reps: lowReps,
      reason: `You hit ${highReps}+ on every set at ${topWeight} kg. Go up to ${next} kg and work back up from ${lowReps}.`,
      kind: 'increase',
    };
  }

  if (minReps < lowReps) {
    const next = apply(topWeight * 0.92);
    return {
      weightKg: next,
      reps: lowReps,
      reason: `You dropped to ${minReps} reps at ${topWeight} kg — below the range. Back off to ${next} kg and rebuild.`,
      kind: 'deload',
    };
  }

  const next = apply(topWeight);
  return {
    weightKg: next,
    reps: Math.min(highReps, minReps + 1),
    reason: `Same ${next} kg as last time — you got ${minReps}. Add a rep on each set before you add weight.`,
    kind: 'hold',
  };
}

export interface ExercisePr {
  bestWeight: number;
  bestWeightReps: number;
  best1rm: number;
  bestVolume: number;
  date: string;
}

export function personalRecord(
  workouts: WorkoutSession[],
  exerciseId: string,
): ExercisePr | undefined {
  let pr: ExercisePr | undefined;

  for (const session of workouts) {
    if (!session.finishedAt) continue;
    for (const logged of session.exercises) {
      if (logged.exerciseId !== exerciseId) continue;

      const sets = workingSets(logged.sets);
      if (sets.length === 0) continue;

      const volume = sets.reduce((sum, s) => sum + s.weightKg * s.reps, 0);
      for (const s of sets) {
        const e1rm = estimate1rm(s.weightKg, s.reps);
        if (!pr || e1rm > pr.best1rm) {
          pr = {
            bestWeight: s.weightKg,
            bestWeightReps: s.reps,
            best1rm: e1rm,
            bestVolume: Math.max(volume, pr?.bestVolume ?? 0),
            date: session.date,
          };
        }
      }
      if (pr && volume > pr.bestVolume) pr.bestVolume = volume;
    }
  }
  return pr;
}

export interface HistoryPoint {
  date: string;
  topWeightKg: number;
  topReps: number;
  /** Estimated 1RM of the best set that day — the fairest single progress number. */
  e1rm: number;
  volume: number;
  sets: number;
}

/**
 * One point per session you did this exercise, oldest first.
 *
 * Tracking top weight alone is misleading — 100×5 and 90×10 are roughly equal
 * efforts but look like a regression. Estimated 1RM collapses weight and reps
 * into one number you can actually plot.
 */
export function exerciseHistory(
  workouts: WorkoutSession[],
  exerciseId: string,
): HistoryPoint[] {
  const points: HistoryPoint[] = [];

  for (const session of workouts) {
    if (!session.finishedAt) continue;

    for (const logged of session.exercises) {
      if (logged.exerciseId !== exerciseId) continue;
      const sets = workingSets(logged.sets);
      if (sets.length === 0) continue;

      let best = sets[0]!;
      let bestE1rm = estimate1rm(best.weightKg, best.reps);
      for (const set of sets) {
        const e1rm = estimate1rm(set.weightKg, set.reps);
        if (e1rm > bestE1rm) {
          best = set;
          bestE1rm = e1rm;
        }
      }

      points.push({
        date: session.date,
        topWeightKg: best.weightKg,
        topReps: best.reps,
        e1rm: bestE1rm,
        volume: sets.reduce((sum, s) => sum + s.weightKg * s.reps, 0),
        sets: sets.length,
      });
    }
  }

  return points.sort((a, b) => (a.date < b.date ? -1 : 1));
}

export interface PrEvent {
  exerciseId: string;
  date: string;
  weightKg: number;
  reps: number;
  e1rm: number;
  /** What the record was before this beat it. Absent for a first-ever entry. */
  previousE1rm?: number;
}

/**
 * Every time you beat your own best on a lift, newest first.
 *
 * A first-ever logged set isn't a record worth celebrating — it's just the
 * first data point — so those are skipped. Only actual improvements count.
 */
export function recentPrs(workouts: WorkoutSession[], limit = 12): PrEvent[] {
  const chronological = [...workouts]
    .filter((w) => w.finishedAt)
    .sort((a, b) => (a.date < b.date ? -1 : 1));

  const best = new Map<string, number>();
  const events: PrEvent[] = [];

  for (const session of chronological) {
    for (const logged of session.exercises) {
      const sets = workingSets(logged.sets);
      if (sets.length === 0) continue;

      for (const set of sets) {
        const e1rm = estimate1rm(set.weightKg, set.reps);
        if (e1rm <= 0) continue;

        const previous = best.get(logged.exerciseId);
        if (previous === undefined) {
          best.set(logged.exerciseId, e1rm);
          continue;
        }
        if (e1rm > previous) {
          best.set(logged.exerciseId, e1rm);
          events.push({
            exerciseId: logged.exerciseId,
            date: session.date,
            weightKg: set.weightKg,
            reps: set.reps,
            e1rm,
            previousE1rm: previous,
          });
        }
      }
    }
  }

  return events.reverse().slice(0, limit);
}

/** Total kg lifted in a session — a rough but useful "how much work" number. */
export function sessionVolume(session: WorkoutSession): number {
  return session.exercises.reduce(
    (total, le) =>
      total + le.sets.filter((s) => s.done).reduce((t, s) => t + s.weightKg * s.reps, 0),
    0,
  );
}

export function sessionSetCount(session: WorkoutSession): number {
  return session.exercises.reduce((n, le) => n + le.sets.filter((s) => s.done).length, 0);
}
